import React, { useEffect, useState, useMemo } from 'react';
import { Flex, IconButton, Loader, Text } from '@vibe/core';
import { Close } from '@vibe/icons';
import { format, parseISO } from 'date-fns';
import { getWorklogs, type Worklog } from '../../api/worklog.api';
import { fetchItemsByIds, fetchBoardsByIds } from '../../api/monday.api';
import { useUsersStore } from '../../stores/usersStore';
import { formatHours } from '../../utils/timesheet-capacity';
import type { TimesheetRow } from '../../types/timesheet.types';
import type { TimesheetGroupBy } from '../../types/timesheet.types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WorklogDetailDialogProps {
  /** The row that was clicked */
  row: TimesheetRow;
  /** The parent row (when a child row is clicked) */
  parentRow?: TimesheetRow;
  /** The date column that was clicked */
  date: string;
  /** Current groupBy mode (to determine what filtering to apply) */
  groupBy: TimesheetGroupBy;
  /** Close callback */
  onClose: () => void;
}

// ─── Enriched Worklog ────────────────────────────────────────────────────────

interface EnrichedWorklog extends Worklog {
  boardName: string;
  itemName: string;
  userName: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Determine which userIds to fetch worklogs for based on the clicked row context.
 * - User-based views (summary, board, item): the user's entityId
 * - Board-based views (board_summary, board_user): when a user child is clicked,
 *   use the child's entityId; otherwise we need all users (handled downstream).
 */
function resolveUserIds(
  row: TimesheetRow,
  parentRow: TimesheetRow | undefined,
  groupBy: TimesheetGroupBy,
): string[] {
  switch (groupBy) {
    case 'summary':
    case 'board':
    case 'item':
      // User timesheets: parent is always user
      if (row.entityType === 'user') return [row.entityId];
      if (parentRow?.entityType === 'user') return [parentRow.entityId];
      return [];

    case 'board_summary':
      // Board summary: no user context, need to fetch all
      return [];

    case 'board_user':
      // Board > User: child row is a user
      if (row.entityType === 'user') return [row.entityId];
      // Parent is board — no user filter
      return [];

    default:
      return [];
  }
}

/**
 * Client-side filter worklogs based on the row context.
 */
function filterWorklogs(
  worklogs: Worklog[],
  row: TimesheetRow,
  parentRow: TimesheetRow | undefined,
  groupBy: TimesheetGroupBy,
): Worklog[] {
  let filtered = worklogs;

  // Filter by board when row/parent represents a board
  const boardId = getBoardId(row, parentRow, groupBy);
  if (boardId) {
    filtered = filtered.filter((w) => w.mondayBoardId === boardId);
  }

  // Filter by item when row represents an item
  const itemId = getItemId(row, groupBy);
  if (itemId) {
    filtered = filtered.filter((w) => w.mondayItemId === itemId);
  }

  // Filter by user when in board views and row is a user child
  const userId = getUserIdForFilter(row, parentRow, groupBy);
  if (userId) {
    filtered = filtered.filter((w) => w.mondayUserId === userId);
  }

  return filtered;
}

function getBoardId(
  row: TimesheetRow,
  parentRow: TimesheetRow | undefined,
  groupBy: TimesheetGroupBy,
): string | null {
  if (groupBy === 'board' && row.entityType === 'board') return row.entityId;
  if (groupBy === 'board_summary') return row.entityId;
  if (groupBy === 'board_user' && row.depth === 0) return row.entityId;
  if (groupBy === 'board_user' && parentRow?.entityType === 'board')
    return parentRow.entityId;
  return null;
}

function getItemId(row: TimesheetRow, groupBy: TimesheetGroupBy): string | null {
  if (groupBy === 'item' && row.entityType === 'item') return row.entityId;
  return null;
}

function getUserIdForFilter(
  row: TimesheetRow,
  parentRow: TimesheetRow | undefined,
  groupBy: TimesheetGroupBy,
): string | null {
  // In board_user view when user child is clicked, parent already filters by board
  // We also need to filter by user
  if (groupBy === 'board_user' && row.entityType === 'user') return row.entityId;
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

const WorklogDetailDialog: React.FC<WorklogDetailDialogProps> = ({
  row,
  parentRow,
  date,
  groupBy,
  onClose,
}) => {
  const [worklogs, setWorklogs] = useState<EnrichedWorklog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getUserById = useUsersStore((s) => s.getUserById);
  const users = useUsersStore((s) => s.users);

  const formattedDate = useMemo(
    () => format(parseISO(date), 'EEEE, MMMM d, yyyy'),
    [date],
  );

  // Build a title based on context
  const dialogTitle = useMemo(() => {
    if (row.depth > 0 && parentRow) {
      return `${parentRow.label} > ${row.label}`;
    }
    return row.label;
  }, [row, parentRow]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const userIds = resolveUserIds(row, parentRow, groupBy);

        // If we have specific userIds, fetch per user; otherwise fetch all
        // worklogs from the relevant users
        let allWorklogs: Worklog[];

        if (userIds.length > 0) {
          // Fetch worklogs for specific user(s) on this date
          const promises = userIds.map((uid) =>
            getWorklogs({
              userId: uid,
              startDate: date,
              endDate: date,
            }),
          );
          const results = await Promise.all(promises);
          allWorklogs = results.flat();
        } else {
          // board_summary or board parent in board_user view —
          // we need all users' worklogs for this date filtered by board
          // Fetch all worklogs for all users that have data on this date
          // We'll use a broader approach: fetch for each user that has data
          const userIdSet = new Set<string>();
          for (const u of users) {
            userIdSet.add(String(u.id));
          }
          // To avoid too many requests, fetch for all known users in parallel batches
          // But for the cell to have data, the user must have logged time
          // Use a reasonable approach: up to 20 parallel requests
          const allUserIds = Array.from(userIdSet).slice(0, 50);
          const batchSize = 10;
          const batches: string[][] = [];
          for (let i = 0; i < allUserIds.length; i += batchSize) {
            batches.push(allUserIds.slice(i, i + batchSize));
          }

          allWorklogs = [];
          for (const batch of batches) {
            const promises = batch.map((uid) =>
              getWorklogs({
                userId: uid,
                startDate: date,
                endDate: date,
              }),
            );
            const results = await Promise.all(promises);
            allWorklogs.push(...results.flat());
          }
        }

        if (cancelled) return;

        // Apply client-side filters
        const filtered = filterWorklogs(allWorklogs, row, parentRow, groupBy);

        // Resolve board and item names
        const uniqueBoardIds = [...new Set(filtered.map((w) => w.mondayBoardId))];
        const uniqueItemIds = [...new Set(filtered.map((w) => w.mondayItemId))];

        const [boards, items] = await Promise.all([
          fetchBoardsByIds(uniqueBoardIds),
          fetchItemsByIds(uniqueItemIds),
        ]);

        if (cancelled) return;

        const boardMap: Record<string, string> = {};
        for (const b of boards) boardMap[b.id] = b.name;

        const itemMap: Record<string, string> = {};
        for (const i of items) itemMap[i.id] = i.name;

        // Enrich worklogs
        const enriched: EnrichedWorklog[] = filtered.map((w) => {
          const user = getUserById(Number(w.mondayUserId));
          return {
            ...w,
            boardName: boardMap[w.mondayBoardId] ?? `Board #${w.mondayBoardId}`,
            itemName: itemMap[w.mondayItemId] ?? `Item #${w.mondayItemId}`,
            userName: user?.name ?? `User #${w.mondayUserId}`,
          };
        });

        // Sort by hours descending
        enriched.sort((a, b) => b.hours - a.hours);

        setWorklogs(enriched);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message ?? 'Failed to load worklogs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [row, parentRow, date, groupBy, getUserById, users]);

  const totalHours = useMemo(
    () => worklogs.reduce((sum, w) => sum + w.hours, 0),
    [worklogs],
  );

  return (
    <div className="rp-ts-dialog-overlay" onClick={onClose}>
      <div
        className="rp-ts-dialog rp-ts-dialog--wide rp-ts-wld"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rp-ts-wld__header">
          <div className="rp-ts-wld__header-info">
            <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
              {dialogTitle}
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {formattedDate}
            </Text>
          </div>
          <Flex align={Flex.align.CENTER} gap={Flex.gaps.SMALL}>
            {!loading && (
              <span className="rp-ts-wld__total-badge">
                {worklogs.length}{' '}
                {worklogs.length === 1 ? 'worklog' : 'worklogs'} |{' '}
                {formatHours(totalHours)}
              </span>
            )}
            <IconButton
              icon={Close}
              size={IconButton.sizes.SMALL}
              onClick={onClose}
              ariaLabel="Close"
            />
          </Flex>
        </div>

        {/* Body */}
        <div className="rp-ts-wld__body">
          {loading ? (
            <div className="rp-ts-wld__loading">
              <Loader size={Loader.sizes.SMALL} />
            </div>
          ) : error ? (
            <div className="rp-ts-wld__error">
              <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                {error}
              </Text>
            </div>
          ) : worklogs.length === 0 ? (
            <div className="rp-ts-wld__empty">
              <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
                No worklogs found.
              </Text>
            </div>
          ) : (
            <div className="rp-ts-wld__list">
              {worklogs.map((wl) => (
                <div key={wl.id} className="rp-ts-wld__entry">
                  <div className="rp-ts-wld__entry-main">
                    <div className="rp-ts-wld__entry-info">
                      <Text
                        type={Text.types.TEXT2}
                        weight={Text.weights.MEDIUM}
                        ellipsis
                      >
                        {wl.itemName}
                      </Text>
                      <Flex
                        align={Flex.align.CENTER}
                        gap={Flex.gaps.XS}
                        style={{ marginTop: '2px' }}
                      >
                        <Text
                          type={Text.types.TEXT2}
                          color={Text.colors.SECONDARY}
                          ellipsis
                        >
                          {wl.boardName}
                        </Text>
                        <span className="rp-ts-wld__separator">|</span>
                        <Text
                          type={Text.types.TEXT2}
                          color={Text.colors.SECONDARY}
                          ellipsis
                        >
                          {wl.userName}
                        </Text>
                        {wl.worklogCategoryName && (
                          <>
                            <span className="rp-ts-wld__separator">|</span>
                            <span
                              className="rp-ts-wld__category-dot"
                              style={{ backgroundColor: wl.worklogCategoryColor ?? '#808080', width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }}
                            />
                            <Text
                              type={Text.types.TEXT2}
                              color={Text.colors.SECONDARY}
                              ellipsis
                            >
                              {wl.worklogCategoryName}
                            </Text>
                          </>
                        )}
                      </Flex>
                    </div>
                    <span className="rp-ts-wld__entry-hours">
                      {formatHours(wl.hours)}
                    </span>
                  </div>
                  {wl.description && (
                    <Text
                      type={Text.types.TEXT2}
                      color={Text.colors.SECONDARY}
                      className="rp-ts-wld__entry-desc"
                    >
                      {wl.description}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorklogDetailDialog;
